import React from "react";
import Select from "react-select";
import { FaPlus, FaSave, FaTimes, FaEdit, FaTrash } from "react-icons/fa";
import CustomTooltip from './../../../Layout/Tooltip/CustomTooltip';

const SubRecipeMainCard = ({
    // Classes
    gridLabelClass,
    editableClass,
    readonlyClass,
    selectStyles,

    // Refs
    recipeNameRef,
    recipeUomRef,
    recipeUnitRef,
    ingredientSelectRef,
    quantityRef,
    uomSelectRef,

    // State
    formData,
    itemOptions,
    uomOptions,
    displayCostItems,
    editingIndex,
    showConversionOptions,

    // Handlers
    handleInputChange,
    handleCostItemSelect,
    handleQuantityChange,
    handleQuantityKeyDown,
    handleUomSelectChange,
    handleUomKeyDown,
    handleAddCostItem,
    handleCancelEdit,
    handleEdit,
    handleDelete,

    // Utils
    formatSalesValue,
    formatNumber,
}) => {
    return (
        <div className="grid grid-cols-5 gap-10 mb-8">

            {/* ── Left panel: inputs ── */}
            <div className="col-span-2">
                <div className="grid grid-cols-3 gap-y-3 gap-x-1 content-start">

                    {/* Sub-recipe name */}
                    <label className={gridLabelClass}>Name</label>
                    <input
                        ref={recipeNameRef}
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); recipeUomRef.current?.focus(); }
                        }}
                        placeholder="Sub-recipe name"
                        className={editableClass}
                    />

                    {/* UOM */}
                    <label className={gridLabelClass}>UOM</label>
                    <input
                        ref={recipeUomRef}
                        type="text"
                        name="uom"
                        value={formData.uom}
                        onChange={handleInputChange}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); recipeUnitRef.current?.focus(); }
                        }}
                        placeholder="e.g. kg, ltr"
                        className={editableClass}
                    />

                    {/* Unit */}
                    <label className={gridLabelClass}>Unit</label>
                    <input
                        ref={recipeUnitRef}
                        type="number"
                        name="unit"
                        value={formData.unit}
                        onChange={handleInputChange}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); ingredientSelectRef.current?.focus(); }
                        }}
                        min="0"
                        step="any"
                        placeholder="e.g. 1000"
                        className={editableClass}
                    />

                    <hr className="col-span-3 my-0" />

                    <label className={gridLabelClass}>Ingredient</label>
                    <div className="col-span-2">
                        <Select
                            ref={ingredientSelectRef}
                            options={itemOptions}
                            value={formData.costItem ? { value: formData.costItem, label: formData.costItem } : null}
                            onChange={handleCostItemSelect}
                            placeholder="Select ingredient"
                            isSearchable
                            isClearable
                            className="basic-single"
                            classNamePrefix="select"
                            styles={selectStyles}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                        />
                    </div>

                    <label className={gridLabelClass}>Rate</label>
                    <input
                        type="text"
                        value={formData.rate ? parseFloat(formData.rate).toFixed(2) : ''}
                        readOnly
                        className={readonlyClass}
                    />

                    <label className={gridLabelClass}>Unit</label>
                    <input
                        type="text"
                        value={
                            formData.itemUnit && formData.itemUom
                                ? `${formData.itemUnit} (${formData.itemUom})`
                                : ''
                        }
                        readOnly
                        className={readonlyClass}
                    />

                    <label className={gridLabelClass}>Quantity</label>
                    <input
                        ref={quantityRef}
                        type="number"
                        name="quantity"
                        value={formData.quantity}
                        onChange={handleQuantityChange}
                        onKeyDown={handleQuantityKeyDown}
                        step="any"
                        min="0"
                        className={editableClass}
                    />

                    <label className={gridLabelClass}>UOM</label>
                    {showConversionOptions && uomOptions.length > 0 ? (
                        <div className="col-span-2">
                            <Select
                                ref={uomSelectRef}
                                options={uomOptions}
                                value={
                                    formData.new_uom
                                        ? uomOptions.find((o) => o.value === formData.new_uom)
                                        : uomOptions[0] ?? null
                                }
                                onChange={handleUomSelectChange}
                                placeholder="Select UOM"
                                isSearchable={false}
                                className="basic-single"
                                classNamePrefix="select"
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                onKeyDown={handleUomKeyDown}
                            />
                        </div>
                    ) : (
                        <input
                            type="text"
                            value={formData.itemUom || ''}
                            readOnly
                            className={readonlyClass}
                        />
                    )}

                    {/* Add / Update / Cancel buttons */}
                    {formData.name && (
                        <div className="col-span-3 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={handleAddCostItem}
                                disabled={!formData.costItem}
                                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
                            >
                                {editingIndex !== null ? <FaSave size={12} /> : <FaPlus size={12} />}
                                {editingIndex !== null ? 'Update' : 'Add'}
                            </button>

                            {editingIndex !== null && (
                                <button
                                    type="button"
                                    onClick={handleCancelEdit}
                                    className="px-3 py-1.5 text-sm bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-1 focus:ring-gray-400 transition-colors flex items-center gap-2"
                                >
                                    <FaTimes size={12} />
                                    Cancel
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* ── Right panel: table ── */}
            <div className="col-span-3">
                {displayCostItems.length > 0 && (
                    <div className="bg-white rounded-sm shadow-md overflow-hidden">
                        <div className="px-3 py-2 border-b bg-gray-50">
                            <div className="text-sm font-semibold text-gray-700">Ingredients</div>
                        </div>

                        <div className="overflow-auto max-h-[420px] tiny-scrollbar">
                            <table className="w-full text-center text-sm">
                                <thead className="bg-gray-100 sticky top-0 z-20">
                                    <tr>
                                        <th className="px-2 py-2.5 font-semibold text-gray-700">Ingredient</th>
                                        <th className="px-2 py-2.5 font-semibold text-gray-700">Qty</th>
                                        <th className="px-2 py-2.5 font-semibold text-gray-700">UOM</th>
                                        <th className="px-2 py-2.5 font-semibold text-gray-700">Cost</th>
                                        <th className="px-2 py-2.5 font-semibold text-gray-700">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {displayCostItems.map((item, index) => (
                                        <tr
                                            key={index}
                                            className={`hover:bg-gray-50 ${item.isDraft ? 'bg-blue-50 opacity-75' : ''}`}
                                        >
                                            <td className="p-2 text-gray-900">
                                                <CustomTooltip
                                                    content={
                                                        <div className="flex flex-col gap-y-1">
                                                            <span>Rate: {formatSalesValue(item.rate)}</span>
                                                            <span>Unit: {item.itemUnit} ({item.itemUom})</span>
                                                        </div>
                                                    }
                                                >
                                                    <span className="cursor-pointer hover:underline">{item.name}</span>
                                                </CustomTooltip>
                                            </td>
                                            <td className="p-2 text-gray-700">{formatNumber(item.quantity)}</td>
                                            <td className="p-2 text-gray-700">{item.new_uom}</td>
                                            <td className="p-2 font-medium text-gray-900">{formatSalesValue(item.cost)}</td>
                                            <td className="p-2">
                                                {item.isDraft ? (
                                                    <span className="text-xs text-blue-500 italic">pending...</span>
                                                ) : (
                                                    <div className="flex justify-center items-center gap-1.5">
                                                        <button
                                                            onClick={() => handleEdit(index)}
                                                            className="h-7 w-7 flex items-center justify-center rounded border border-gray-300 text-blue-600 hover:bg-blue-50 transition-colors"
                                                        >
                                                            <FaEdit className="h-3 w-3" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(index)}
                                                            className="h-7 w-7 flex items-center justify-center rounded border border-gray-300 text-red-600 hover:bg-red-50 transition-colors"
                                                        >
                                                            <FaTrash className="h-3 w-3" />
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

        </div>
    )
}

export default SubRecipeMainCard